import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { organizationApi } from 'src/api/organization';
import { organizationProfessionalConfigApi } from 'src/api/organization-details';
import { specializationApi } from 'src/api/specialization';
import { useUserContext } from 'src/auth/useUserContext';
import OrganizationDetailsForm from 'src/sections/@dashboard/organization/components/OrganizationDetails/OrganizationDetailsForm/OrganizationDetailsForm';
import OrganizationDetailsProfessionalForm from 'src/sections/@dashboard/organization/components/OrganizationDetails/OrganizationDetailsForm/OrganizationDetailsProfessionalForm';
import OrganizationDetailsFormSkeleton from 'src/sections/@dashboard/organization/components/OrganizationDetails/OrganizationDetailsFormSkeleton';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';

export default function OrganizationDetailsTabsPage() {
  const { organizationId } = useParams();
  const { isProfessionalRoleSelected } = useOrganizationContext();
  const { data: user } = useUserContext();
  const { data, isLoading, refetch } = useQuery(
    ['organization', organizationId],
    () =>
      organizationApi.organizationControllerGetOrganizationById({
        id: organizationId!,
      }),
    {
      retry: 2,
      enabled: !!organizationId,
    },
  );

  const {
    data: organizationDetails,
    isLoading: isLoadingOrganizationDetails,
    refetch: refetchOrganizationDetails,
  } = useQuery(
    [
      'organization-professional-config',
      organizationId,
      user?.id,
      isProfessionalRoleSelected,
    ],
    () =>
      organizationProfessionalConfigApi.organizationProfessionalConfigControllerGetConfig(
        {
          organizationId: organizationId!,
          professionalId: user!.id,
        },
      ),
    {
      retry: 2,
      enabled: !!organizationId && !!user?.id && isProfessionalRoleSelected,
    },
  );

  const { data: specializations, isLoading: isLoadingSpecializations } =
    useQuery(
      ['specializations', organizationId],
      () => specializationApi.specializationControllerGetAllSpecializations(),
      {
        retry: 2,
        staleTime: 5 * 60 * 1000,
        cacheTime: 5 * 60 * 1000,
      },
    );
  const organization = data?.data;

  if (
    isLoading ||
    (isLoadingOrganizationDetails && isProfessionalRoleSelected) ||
    isLoadingSpecializations
  ) {
    return <OrganizationDetailsFormSkeleton />;
  }

  if (!organization) {
    return null;
  }

  const allSpecializationsData = specializations?.data || [];

  // Filter specializations to only include those enabled in the organization
  const organizationSpecializationIds = organization.specializations || [];
  const availableSpecializationsForProfessional = allSpecializationsData.filter(
    (spec) => organizationSpecializationIds.includes(spec.id),
  );

  return isProfessionalRoleSelected ? (
    <OrganizationDetailsProfessionalForm
      fetchOrganizationDetails={refetchOrganizationDetails}
      organizationDetails={organizationDetails?.data}
      specializations={availableSpecializationsForProfessional}
    />
  ) : (
    <OrganizationDetailsForm
      organization={organization}
      fetchOrganization={refetch}
      specializations={allSpecializationsData}
    />
  );
}
